import { useMutation, useQueryClient } from "react-query";

import { RestError } from "../../services/rest-service/RestService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";

import { keys } from "./Keys";
import { IReportTaskSummaryDTO } from "../../services/reporting-data-service/ReportScheduleModels";
import ReportingDataService from "../../services/reporting-data-service/ReportingDataService";

type MutationContextType = {
    previousInstances: IReportTaskSummaryDTO[] | undefined;
};

export type UseDeleteScheduledReportParams = {
    organizationID: number,
    groupID: number,
} & BaseUseMutationParameters<void, number>;

export function useDeleteScheduledReport({
    organizationID,
    groupID,
    onError,
    onSuccess
}: UseDeleteScheduledReportParams) {
    const queryClient = useQueryClient();

    return useMutation<void, RestError, number, MutationContextType>(
        async (item) => {
            await ReportingDataService.deleteReportTask(
                groupID,
                item,
                true
            );
        },
        {
            onMutate: async (item: number) => {

                await queryClient.cancelQueries(keys.all);

                const previousInstances = queryClient.getQueryData<IReportTaskSummaryDTO[]>(
                    keys.taskList(
                        organizationID,
                        groupID
                    )
                );

                queryClient.setQueryData<IReportTaskSummaryDTO[]>(
                    keys.taskList(
                        organizationID,
                        groupID
                    ),
                    (old?: IReportTaskSummaryDTO[]) => {
                        return old?.filter(t => t.id !== item) ?? [];
                    }
                );

                return {
                    previousInstances
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _runTicket, context) => {

                if (error && context?.previousInstances) {
                    queryClient.setQueryData<IReportTaskSummaryDTO[]>(
                        keys.taskList(
                            organizationID,
                            groupID,
                        ),
                        context?.previousInstances
                    );
                }

                queryClient.invalidateQueries(keys.taskList(organizationID, groupID));
            }
        }
    );
}
